import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { businessPageAccess } from '../../lib/roles';
import BusinessHeader from '../../components/business/BusinessHeader';
import ConfirmDialog from '../../components/business/ConfirmDialog';
import { Plus, Edit2, Archive, RotateCcw, Pause, Play, Eye, Gift, CheckCircle2 } from 'lucide-react';
import { businessApi, errorMessage } from '../../lib/api';

import { flag } from '../../config';

const BusinessDeals = () => {
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [deals, setDeals] = useState([]);
  const [archivedDeals, setArchivedDeals] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [userBusinesses, setUserBusinesses] = useState([]);
  const [currentBusiness, setCurrentBusiness] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [dealToArchive, setDealToArchive] = useState(null);
  const [archiving, setArchiving] = useState(false);
  const [error, setError] = useState('');

  // Check if user has business access (role logic: lib/roles.js businessPageAccess)
  const hasBusinessAccess = businessPageAccess(user);

  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !hasBusinessAccess)) {
      navigate('/');
    }
  }, [isAuthenticated, authLoading, user, navigate, hasBusinessAccess]);

  useEffect(() => {
    if (isAuthenticated && hasBusinessAccess) {
      fetchAllData();
    }
  }, [isAuthenticated, user, hasBusinessAccess]);

  const fetchAllData = async () => {
    try {
      setLoading(true);

      const [businessesRes, profileRes, dealsRes, subRes] = await Promise.all([
        flag('MULTI_BUSINESS') ? businessApi.get('/business/my-businesses') : Promise.resolve({ data: { businesses: [] } }),
        businessApi.get('/business/profile'),
        businessApi.get('/business/deals'),
        // The cap is a convenience; the server enforces it either way.
        businessApi.get('/business/subscription').catch(() => ({ data: null }))
      ]);

      setUserBusinesses(businessesRes.data.businesses || []);
      setCurrentBusiness(profileRes.data);
      setSubscription(subRes.data);

      const allDeals = dealsRes.data || [];
      setDeals(allDeals.filter(d => !d.archived));
      setArchivedDeals(allDeals.filter(d => d.archived));
      
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load deals. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchBusiness = async (businessId) => {
    try {
      await businessApi.post(`/business/switch/${businessId}`, {});
      window.location.reload();
    } catch (error) {
      console.error('Error switching business:', error);
    }
  };

  const handlePauseDeal = async (dealId, isPaused) => {
    try {
      await businessApi.patch(`/business/deals/${dealId}/pause?is_paused=${!isPaused}`, {});
      fetchAllData();
      toast.success(isPaused ? 'Deal resumed.' : 'Deal paused.');
    } catch (error) {
      console.error('Error toggling deal pause status:', error);
      toast.error(errorMessage(error, 'Could not update this deal.'));
    }
  };

  // Archiving hides a live deal, so it asks first; restoring does not (audit rows 55, 73).
  const handleArchiveDeal = async () => {
    if (!dealToArchive) return;
    setArchiving(true);
    try {
      await businessApi.delete(`/business/deals/${dealToArchive.id}`);
      setDealToArchive(null);
      fetchAllData();
      toast.success('Deal archived.');
    } catch (error) {
      console.error('Error archiving deal:', error);
      toast.error(errorMessage(error, 'Failed to archive this deal.'));
    } finally {
      setArchiving(false);
    }
  };

  const handleRestoreDeal = async (dealId) => {
    try {
      await businessApi.patch(`/business/deals/${dealId}/restore`, {});
      fetchAllData();
      toast.success('Deal restored.');
    } catch (error) {
      console.error('Error restoring deal:', error);
      toast.error(errorMessage(error, 'Failed to restore this deal.'));
    }
  };

  const displayDeals = showArchived ? archivedDeals : deals;
  const dealLimit = subscription?.deal_limit ?? null;
  const dealsUsed = subscription?.deals_used ?? deals.length;
  const atDealLimit = dealLimit != null && dealsUsed >= dealLimit;
  const capLabel = dealLimit != null ? `${dealsUsed} of ${dealLimit} deals used` : null;
  const capHint = dealLimit != null
    ? `Your plan covers ${dealLimit} active deal${dealLimit === 1 ? '' : 's'} — archive one or switch plan to add more.`
    : undefined;

  // Loading State
  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FEF7ED' }}>
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#8b5cf6] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p style={{ color: 'rgba(30, 27, 75, 0.5)' }}>Loading deals...</p>
        </div>
      </div>
    );
  }

  // No Business State
  if (!currentBusiness) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#FEF7ED' }}>
        <BusinessHeader
          user={user}
          userName={`${user?.first_name || ''} ${user?.last_name || ''}`}
          currentBusiness={currentBusiness}
          businesses={userBusinesses}
          onSwitchBusiness={handleSwitchBusiness}
        />
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-12">
          <div 
            className="bg-white rounded-xl p-8 text-center"
            style={{ 
              border: '1px solid rgba(30, 27, 75, 0.05)',
              boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)'
            }}
          >
            <h2 className="text-[24px] text-[#1e1b4b] mb-4" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}>
              No Business Found
            </h2>
            <p className="mb-6" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>You need to set up a business profile first.</p>
            <button
              onClick={() => navigate('/business/settings')}
              className="px-6 py-3 text-white font-semibold rounded-xl transition-all duration-200 hover:-translate-y-0.5"
              style={{ 
                background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4), 0 0 30px rgba(139, 92, 246, 0.2)'
              }}
            >
              Set Up Business
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FEF7ED' }}>
      {/* V5 Business Header */}
      <BusinessHeader 
        user={user}
        userName={`${user?.first_name || ''} ${user?.last_name || ''}`}
        currentBusiness={currentBusiness}
        businesses={userBusinesses}
        onSwitchBusiness={handleSwitchBusiness}
      />

      {/* Main Content */}
      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-8">
        {/* Page Header - V5 Design Language */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <p className="font-mono text-xs mb-2" style={{ color: '#8b5cf6' }}>// manage_deals</p>
            <h1 className="text-[32px] text-[#1e1b4b]" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}>
              Your Deals
            </h1>
            <p className="mt-2" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>Create and manage deals for your business</p>
          </div>
          {/* Primary Button - Purple Gradient per V5 */}
          <div className="sm:text-right">
            <button
              onClick={() => navigate('/business/deals/new')}
              disabled={atDealLimit}
              title={atDealLimit ? capHint : undefined}
              className="w-full sm:w-auto px-6 py-3 min-h-[44px] text-white font-semibold rounded-xl transition-all duration-200 hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              style={{
                background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4), 0 0 30px rgba(139, 92, 246, 0.2)'
              }}
            >
              <Plus className="w-5 h-5" />
              New Deal
            </button>
            {capLabel && (
              <p className="mt-2 text-[13px]" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
                {atDealLimit ? capHint : capLabel}
              </p>
            )}
          </div>
        </div>

        {/* Error Message - V5 Colors */}
        {error && (
          <div 
            className="mb-6 p-4 rounded-xl flex items-center gap-3"
            style={{ 
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.2)',
              color: '#ef4444'
            }}
          >
            <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-[14px] font-medium">{error}</p>
          </div>
        )}

        {/* Main Card - V5 Design Language */}
        <div 
          className="bg-white rounded-xl p-6"
          style={{ 
            border: '1px solid rgba(30, 27, 75, 0.05)',
            borderLeft: '4px solid #8b5cf6',
            boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)'
          }}
        >
          {/* Filter Tabs - Purple active state per V5 */}
          <div className="flex justify-end mb-6">
            <div className="flex gap-2">
              <button
                onClick={() => setShowArchived(false)}
                className={`px-4 py-2 rounded-full text-[14px] font-semibold transition-all duration-200 ${
                  !showArchived ? 'text-white' : ''
                }`}
                style={!showArchived 
                  ? { backgroundColor: '#8b5cf6', boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4), 0 0 30px rgba(139, 92, 246, 0.2)' }
                  : { color: 'rgba(30, 27, 75, 0.5)' }
                }
              >
                Active ({deals.length})
              </button>
              <button
                onClick={() => setShowArchived(true)}
                className={`px-4 py-2 rounded-full text-[14px] font-semibold transition-all duration-200 ${
                  showArchived ? 'text-white' : ''
                }`}
                style={showArchived 
                  ? { backgroundColor: '#8b5cf6', boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4), 0 0 30px rgba(139, 92, 246, 0.2)' }
                  : { color: 'rgba(30, 27, 75, 0.5)' }
                }
              >
                Archived ({archivedDeals.length})
              </button>
            </div>
          </div>

          {/* Deal Cards */}
          <div className="space-y-4">
            {displayDeals.length === 0 ? (
              <div className="text-center py-12" style={{ color: 'rgba(30, 27, 75, 0.4)' }}>
                {showArchived ? 'No archived deals' : 'No active deals yet. Create your first deal!'}
              </div>
            ) : (
              displayDeals.map((deal) => (
                <div 
                  key={deal.id}
                  className="p-5 rounded-xl transition-all duration-200 hover:-translate-y-0.5"
                  style={{ 
                    backgroundColor: 'rgba(30, 27, 75, 0.02)',
                    border: '1px solid rgba(30, 27, 75, 0.05)'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 8px rgba(30, 27, 75, 0.06)'}
                  onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-[#1e1b4b] text-[18px]">{deal.title}</h3>
                        {/* Status Badge - V5 Colors */}
                        {deal.is_paused ? (
                          <span 
                            className="px-3 py-0.5 text-[12px] font-bold rounded-full"
                            style={{ backgroundColor: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }}
                          >
                            Paused
                          </span>
                        ) : !showArchived && (
                          <span 
                            className="px-3 py-0.5 text-[12px] font-bold rounded-full"
                            style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)', color: '#22c55e' }}
                          >
                            Active
                          </span>
                        )}
                      </div>
                      {deal.description && (
                        <p className="text-[14px] line-clamp-2" style={{ color: 'rgba(30, 27, 75, 0.6)' }}>{deal.description}</p>
                      )}
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex items-center gap-2 ml-4">
                      {!showArchived ? (
                        <>
                          <button
                            onClick={() => handlePauseDeal(deal.id, deal.is_paused)}
                            className="p-2.5 min-w-[40px] min-h-[40px] rounded-lg transition-all duration-200 flex items-center justify-center"
                            style={{ color: 'rgba(30, 27, 75, 0.4)' }}
                            onMouseEnter={(e) => { e.currentTarget.style.color = '#8b5cf6'; e.currentTarget.style.backgroundColor = 'rgba(139, 92, 246, 0.1)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(30, 27, 75, 0.4)'; e.currentTarget.style.backgroundColor = 'transparent'; }}
                            aria-label={deal.is_paused ? 'Resume deal' : 'Pause deal'} title={deal.is_paused ? 'Resume Deal' : 'Pause Deal'}
                          >
                            {deal.is_paused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => navigate(`/business/deals/edit/${deal.id}`)}
                            className="p-2.5 min-w-[40px] min-h-[40px] rounded-lg transition-all duration-200 flex items-center justify-center"
                            style={{ color: 'rgba(30, 27, 75, 0.4)' }}
                            onMouseEnter={(e) => { e.currentTarget.style.color = '#8b5cf6'; e.currentTarget.style.backgroundColor = 'rgba(139, 92, 246, 0.1)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(30, 27, 75, 0.4)'; e.currentTarget.style.backgroundColor = 'transparent'; }}
                            aria-label="Edit deal" title="Edit Deal"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setDealToArchive(deal)}
                            className="p-2.5 min-w-[40px] min-h-[40px] rounded-lg transition-all duration-200 flex items-center justify-center"
                            style={{ color: 'rgba(30, 27, 75, 0.4)' }}
                            onMouseEnter={(e) => { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(30, 27, 75, 0.4)'; e.currentTarget.style.backgroundColor = 'transparent'; }}
                            aria-label="Archive deal" title="Archive Deal"
                          >
                            <Archive className="w-4 h-4" />
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => handleRestoreDeal(deal.id)}
                          className="p-2.5 min-w-[40px] min-h-[40px] rounded-lg transition-all duration-200 flex items-center justify-center"
                          style={{ color: 'rgba(30, 27, 75, 0.4)' }}
                          onMouseEnter={(e) => { e.currentTarget.style.color = '#22c55e'; e.currentTarget.style.backgroundColor = 'rgba(34, 197, 94, 0.1)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(30, 27, 75, 0.4)'; e.currentTarget.style.backgroundColor = 'transparent'; }}
                          aria-label="Restore deal" title="Restore Deal"
                        >
                          <RotateCcw className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Deal Stats */}
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[14px] pt-3" style={{ color: 'rgba(30, 27, 75, 0.5)', borderTop: '1px solid rgba(30, 27, 75, 0.05)' }}>
                    <span className="flex items-center gap-1.5">
                      <Eye className="w-4 h-4" /> {deal.total_views ?? 0} views
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Gift className="w-4 h-4" /> {deal.total_claims || 0} claims
                    </span>
                    <span className="flex items-center gap-1.5">
                      <CheckCircle2 className="w-4 h-4" /> {deal.total_redemptions || 0} redeemed
                    </span>
                    {deal.start_date && (
                      <span>
                        {new Date(deal.start_date).toLocaleDateString()} - {deal.end_date ? new Date(deal.end_date).toLocaleDateString() : 'Ongoing'}
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Create New Deal CTA - Dashed Button per V5 */}
          {!showArchived && deals.length > 0 && (
            atDealLimit ? (
              <p className="w-full mt-6 py-3 text-center text-[14px]" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
                {capHint}
              </p>
            ) : (
              <button
                onClick={() => navigate('/business/deals/new')}
                className="w-full mt-6 py-3 min-h-[44px] rounded-xl font-semibold flex items-center justify-center gap-2 transition-all duration-200"
                style={{
                  border: '2px dashed rgba(139, 92, 246, 0.3)',
                  color: '#8b5cf6'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(139, 92, 246, 0.05)'; e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.5)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.3)'; }}
              >
                <Plus className="w-5 h-5" />
                Create Another Deal
              </button>
            )
          )}
        </div>
      </div>

      <ConfirmDialog
        open={!!dealToArchive}
        title="Archive this deal?"
        message={dealToArchive ? `"${dealToArchive.title}" stops showing at your venues. You can restore it from the Archived tab.` : ''}
        confirmLabel="Archive deal"
        busy={archiving}
        onConfirm={handleArchiveDeal}
        onCancel={() => setDealToArchive(null)}
      />
    </div>
  );
};

export default BusinessDeals;
